import { useCurrentUser, useMyDecisionDetails, useRbacPrimaryRolePermissions } from "../../api/hooks";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

export const useCompensationAccess = (cycleId: string | undefined) => {
	const { data: currentUser } = useCurrentUser();
	const userID = currentUser?.me?.authorization?.payload?.user_id;

	const { value: compensationEnabled, query: featureFlagQuery } = useFeatureFlagQuery("ff-compensation", undefined);
	const { data: permissions, isLoading: isPermissionsLoading } = useRbacPrimaryRolePermissions("compensation");

	const isLoading = isPermissionsLoading || (compensationEnabled === undefined && !featureFlagQuery.isError);

	const canMakeDecisions = Boolean(permissions?.includes("compensation:makeDecisions"));
	const canViewCycles = Boolean(permissions?.includes("compensation:viewCycles"));
	const canViewCyclesWithDecisionsToMake = Boolean(
		permissions?.includes("compensation:viewCyclesWithDecisionsToMake")
	);
	const canCreateCycle = Boolean(permissions?.includes("compensation:createCycle"));

	const canViewCompensation = compensationEnabled === true && (canViewCycles || canViewCyclesWithDecisionsToMake);

	const compensationDisabled = compensationEnabled === false || featureFlagQuery.isError;

	const canLoadMyDecisions = Boolean(userID && cycleId) && canMakeDecisions && compensationEnabled === true;

	const {
		data: myDecisionsData,
		isLoading: isLoadingMyDecisions,
		isError: isMyDecisionsError,
		isPending: isMyDecisionsPending,
	} = useMyDecisionDetails(userID ?? "", cycleId ?? "", {
		enabled: canLoadMyDecisions,
	});

	const hasDecisionsToMake = (myDecisionsData?.directReportsToMakeDecisionsFor?.results?.length ?? 0) > 0;

	return {
		userID,
		permissions,
		isLoading,
		compensationEnabled,
		compensationDisabled,
		canMakeDecisions,
		canViewCycles,
		canViewCyclesWithDecisionsToMake,
		canCreateCycle,
		canViewCompensation,
		canLoadMyDecisions,
		myDecisionsData,
		isLoadingMyDecisions,
		isMyDecisionsError,
		isMyDecisionsPending,
		hasDecisionsToMake,
	};
};
